import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { PointRuleInput } from "@core/types";
import * as api from "./rulesApi";
import type { RuleScope } from "./rulesApi";

const listKey = (scope: RuleScope) =>
  ["portal", "points-engine", scope] as const;
const historyKey = (ruleId: string) =>
  ["portal", "points-engine", "history", ruleId] as const;
const catalogKey = (scope: RuleScope) =>
  ["portal", "points-engine", "catalog", scope] as const;

export const usePointRules = (scope: RuleScope) =>
  useQuery({
    queryKey: listKey(scope),
    queryFn: () => api.listPointRules(scope),
  });

// EXTRA (human-resolution-spec-212.json): solo se llama cuando el editor se
// abre en modo crear (`enabled`), nunca en modo editar — `PointRule.action`
// ya trae lo necesario para listar y editar.
export const useActionCatalog = (scope: RuleScope, enabled: boolean) =>
  useQuery({
    queryKey: catalogKey(scope),
    queryFn: () => api.listActionCatalog(scope),
    enabled,
  });

// A6 (human-resolution-spec-212.json): la tarjeta de historial del módulo se
// puebla uniendo en cliente los historiales por regla — no existe un
// endpoint de historial del módulo completo, solo por regla.
export const useRulesHistories = (ruleIds: string[]) =>
  useQueries({
    queries: ruleIds.map((ruleId) => ({
      queryKey: historyKey(ruleId),
      queryFn: () => api.pointRuleHistory(ruleId),
    })),
  });

export function usePointRuleMutations() {
  const client = useQueryClient();
  // A4: la tarjeta de impacto se refresca invalidando la query tras guardar,
  // nunca recalculando en el cliente.
  const invalidate = () =>
    client.invalidateQueries({ queryKey: ["portal", "points-engine"] });
  return {
    create: useMutation({
      mutationFn: (input: PointRuleInput) => api.createPointRule(input),
      onSuccess: invalidate,
    }),
    edit: useMutation({
      mutationFn: ({ id, input }: { id: string; input: PointRuleInput }) =>
        api.editPointRule(id, input),
      onSuccess: invalidate,
    }),
    inactivate: useMutation({
      mutationFn: ({ id, confirmed }: { id: string; confirmed: boolean }) =>
        api.inactivatePointRule(id, confirmed),
      onSuccess: invalidate,
    }),
  };
}
